import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { PermissionsEnums } from "../../../../dtos/permissions-enums";
import { PolicyBlob } from "../../../../dtos/policy-blob";
import { PolicyQuoteStatusEnum } from "../../../../dtos/policy-quote-status-enum";
import { PolicyStatusEnums } from "../../../../dtos/policy-status-enums";
import { SubSideNavItemDestinationsEnum } from "../../../../dtos/sub-side-nav-item-destinations-enum";
import { useApiPost, usePermissions } from "../../../../hooks";
import { useAtomFamily } from "../../../../hooks/useAtomFamily";
import { isAPITotallyComplete } from "../../../../utilities/apiFunctions";
import {
  conditionHasValue,
  isEmptyValue,
} from "../../../../utilities/conditionalSupportFunctions";
import {
  INSURED_ATOM_KEY,
  INSURED_BODY_SECTION,
} from "../../../../utilities/queryStringsHash";
import { getQueryStringsURL } from "../../../../utilities/URLUtilities_OBSOLETE";
import { Button } from "../../../TrueUI";
import {
  GlobalInsuredAtomFamily,
  RefreshInsuredHeaderAtom,
} from "../../InsuredAtoms";
import { usePolicyQuoteTriggerComponent } from "../hooks/usePolicyQuoteTriggerComponent";
import { PolicyQuoteInsuredAndActiveSetterProps } from "../PolicyQuoteForm/PolicyQuoteTypes";
import {
  getPolicyQuote,
  policyInformationReset,
} from "../PolicyQuoteForm/PolicyQuoteUtils";
import { getAtomUpdatedByBindPolicy } from "../PolicyQuoteInformation/PolicyInformation/InformationUtils";
import { evaluateRule } from "../PolicyValidation/ValidationEngineFunctions";
import {
  updateInsuredAtom,
  updateInsuredAtomMultiTarget,
  updatePolicyQuoteInformationMultiTarget,
} from "../updatesPolicyQuoteFunctions";
import ErrorBody from "./ErrorBody";
import ModalBindPolicy from "./ModalBindPolicy";
import { BindInstructionsInfoAtom } from "./PolicyQuoteBindInstructionsAtoms";
import SuccessBody from "./SuccessBody";
import styles from "./PolicyQuoteBindInstructionsStyles.module.css";

const PolicyQuoteBindInstructionsBindPolicy: FC<
  PolicyQuoteInsuredAndActiveSetterProps
> = ({ tabKey, insuredId, readOnly }) => {
  const navigate = useNavigate();
  const hasPermission = usePermissions([
    PermissionsEnums.UNDERWRITTER,
    PermissionsEnums.PROGRAM_ADMIN,
  ]);
  const insuredIdAtomKey = `${INSURED_ATOM_KEY} ${tabKey}`;
  const { getAtom, setAtom } = useAtomFamily(
    GlobalInsuredAtomFamily(insuredIdAtomKey)
  );
  const bindInstructionsInfo = useRecoilValue(BindInstructionsInfoAtom);
  const setRefreshInsuredHeader = useSetRecoilState(RefreshInsuredHeaderAtom);
  const { setPolicyQuoteTriggers } = usePolicyQuoteTriggerComponent();

  const [showBindPolicyModal, setShowBindPolicyModal] = useState(false);
  const [bindPolicyErrors, setBindPolicyErrors] = useState<string | null>(null);

  const policyJSON = getAtom()?.policyQuoteInformation?.policyQuote;
  const validations = policyJSON?.configuration?.programValidationList ?? [];

  const validValidations = validations.filter((validation) =>
    evaluateRule(validation.validationQuery, policyJSON)
  );

  const hasValidation = validValidations?.length > 0;
  const {
    responsePost: responseBindPolicy,
    validatorErrorResponse: validatorErrorBindPolicy,
    dispatchPost: dispatchBindPolicy,
  } = useApiPost<PolicyBlob>(
    "api/QuotePolicy/BindInitialQuote",
    getAtom()?.policyQuoteInformation?.policyQuote
  );

  const returnToHistoryTable = () => {
    const atomValue = getAtom();
    const newAtomValue = updateInsuredAtom(
      atomValue ?? {},
      policyInformationReset
    );
    setAtom(newAtomValue);
    setPolicyQuoteTriggers(["policyQuoteHeaderComponent"]);

    const historyHashes = getQueryStringsURL([
      {
        nameOfHash: INSURED_BODY_SECTION,
        valueOfHash: SubSideNavItemDestinationsEnum.HISTORY,
      },
    ]);
    navigate(historyHashes);
  };

  const bindPolicyAction = () => {
    const atomValue = getAtom();
    const policyQuote = getPolicyQuote(atomValue);

    const policyStatus = policyQuote?.configuration?.policyStatusList.find(
      (status) => status.intValue === PolicyStatusEnums.ISSUED
    );
    const policyQuoteStatus =
      policyQuote?.configuration?.policyQuoteStatusList.find(
        (status) => status.intValue === PolicyQuoteStatusEnum.BOUND
      );

    const newAtomValue = getAtomUpdatedByBindPolicy(
      atomValue,
      policyStatus,
      policyQuoteStatus
    );

    setAtom(newAtomValue);
    setBindPolicyErrors(null);
    dispatchBindPolicy();
  };

  const actionsAfterSuccessfulBind = () => {
    setBindPolicyErrors(null);
    const atomValue = getAtom();
    const policyBlob = responseBindPolicy.axiosResponse?.data ?? null;
    const newAtomValue = updatePolicyQuoteInformationMultiTarget(
      atomValue,
      ["policyQuote", "readOnly"],
      [policyBlob, true]
    );
    const newInsuredAtomValue = updateInsuredAtomMultiTarget(
      newAtomValue,
      ["insuredCode", "insuredStatus"],
      [policyBlob?.insured?.insuredCode, policyBlob?.insured?.insuredStatus]
    );
    setAtom(newInsuredAtomValue);
    setShowBindPolicyModal(true);
    setPolicyQuoteTriggers(["policyQuoteReadOnlyComponent"]);
    setRefreshInsuredHeader({
      insuredId,
      insuredCode: policyBlob?.insured?.insuredCode,
      insuredStatus: policyBlob?.insured?.insuredStatus,
    });

    // TODO - Send portal invitation email for the primary contact.
    //   if (
    //     (atomValue?.policyQuoteInformation?.policyQuote?.program
    //       ?.portalUserInviteOnBind ?? false) === true
    //   ) {
    //       Send email
    //   }
  };

  const actionsAfterFailBind = () => {
    const errorMsg =
      validatorErrorBindPolicy?.message ??
      validatorErrorBindPolicy?.title ??
      "";
    setBindPolicyErrors(errorMsg);
    setShowBindPolicyModal(true);
    // TODO - Send email to support@experiencetrue.com that a bind failed, support is ready.
  };

  useEffect(() => {
    if (
      isAPITotallyComplete(responseBindPolicy) &&
      !conditionHasValue(validatorErrorBindPolicy)
    ) {
      actionsAfterSuccessfulBind();
    }
    if (conditionHasValue(validatorErrorBindPolicy)) {
      actionsAfterFailBind();
    }
  }, [responseBindPolicy]);

  return (
    <div className="bind-container">
      <div className={styles.policy_quote_bind_button_container}>
        {hasPermission && !isEmptyValue(bindInstructionsInfo?.payPlan) ? (
          <Button
            size="large"
            className={styles.policy_quote_bind_button}
            sx={{ marginLeft: 1, marginRight: 1 }}
            onClick={() => bindPolicyAction()}
            isDisabled={readOnly || hasValidation}
          >
            bind policy
          </Button>
        ) : null}
      </div>
      <ModalBindPolicy
        showModal={showBindPolicyModal}
        onClose={() => setShowBindPolicyModal(false)}
        onCloseQuote={returnToHistoryTable}
        hasError={!isEmptyValue(bindPolicyErrors)}
        modalBody={
          !isEmptyValue(bindPolicyErrors) ? (
            <ErrorBody bindPolicyErrors={bindPolicyErrors} />
          ) : (
            <SuccessBody />
          )
        }
      />
    </div>
  );
};
export default PolicyQuoteBindInstructionsBindPolicy;
